import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { useAuth } from './AuthContext';
import {
  registerServiceWorker,
  getOrCreatePushSubscription,
  serializePushSubscription,
  getPushSupportStatus,
  setVapidPublicKey,
  hasPushSubscriptions
} from '../utils/pushService';

const NotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  loading: false,
  preferences: null,
  refresh: () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  deleteNotification: async () => {},
  openNotificationPanel: () => {},
  ensurePushSubscription: async () => ({ success: false }),
  pushSupport: { supported: false, reason: 'unknown' },
  hasServerPushSubscription: false,
  showReminder: false,
  setShowReminder: () => {},
  dismissReminder: () => {},
  dismissReminderForToday: () => {}
});

export const NotificationProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [pushSupport, setPushSupport] = useState({ supported: false, reason: 'unknown' });
  const [vapidReady, setVapidReady] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const initialLoadRef = useRef(true);
  const lastSeenIdRef = useRef(null);
  const audioCtxRef = useRef(null);
  const audioUnlockedRef = useRef(false);
  const pollingRef = useRef(null);
  const serviceWorkerReadyRef = useRef(false);
  const reminderCheckIntervalRef = useRef(null);
  const metaLoadedRef = useRef(false);
  const notificationFetchInProgressRef = useRef(false);
  const pushSyncInProgressRef = useRef(false);
  const pushSyncedUserRef = useRef(null);

  const LAST_SEEN_STORAGE_KEY = 'jansoft-last-seen-notification-id';

  const readLastSeenIdFromStorage = useCallback(() => {
    try {
      if (typeof window === 'undefined') return null;
      const stored = localStorage.getItem(LAST_SEEN_STORAGE_KEY);
      if (!stored) return null;
      const parsed = parseInt(stored, 10);
      return Number.isFinite(parsed) ? parsed : null;
    } catch (_) {
      return null;
    }
  }, []);

  const writeLastSeenIdToStorage = useCallback((id) => {
    try {
      if (typeof window === 'undefined' || id == null) return;
      localStorage.setItem(LAST_SEEN_STORAGE_KEY, String(id));
    } catch (_) {
      // Sessizce atlanıyor
    }
  }, []);

  // localStorage'dan son görülen bildirim ID'sini yükle
  useEffect(() => {
    const storedId = readLastSeenIdFromStorage();
    if (storedId != null) {
      lastSeenIdRef.current = storedId;
    }
  }, [readLastSeenIdFromStorage]);

  const calculateUnread = useCallback((items = []) => {
    return items.filter((item) => !item.isRead).length;
  }, []);

  const fetchNotifications = useCallback(async (options = {}) => {
    const includeMeta = options?.includeMeta === true;
    const silent = options?.silent === true;
    if (!user || authLoading) return;
    
    const token = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
    if (!token) return;
    
    if (notificationFetchInProgressRef.current) {
      return;
    }

    notificationFetchInProgressRef.current = true;
    try {
      if (!silent) {
        setLoading(true);
      }
      
      let notificationRes = { data: { data: { items: [] } } };
      const shouldLoadMeta = includeMeta || !metaLoadedRef.current;
      let prefRes = null;
      let vapidRes = null;
      
      try {
        notificationRes = await api.get('/api/notifications?limit=20');
      } catch (err) {
        if (err?.response?.status !== 401) {
          // 401 dışındaki hatalar sessizce atlanıyor
        }
      }
      
      if (shouldLoadMeta) {
        try {
          prefRes = await api.get('/api/notifications/preferences');
        } catch (err) {
          if (err?.response?.status !== 401) {
            // 401 dışındaki hatalar sessizce atlanıyor
          }
        }

        try {
          vapidRes = await api.get('/api/notifications/vapid-key');
        } catch (err) {
          // VAPID key hatası önemli değil
        }
      }
      
      const items = notificationRes.data?.data?.items || [];
      setNotifications(items);
      setUnreadCount(calculateUnread(items));
      if (shouldLoadMeta) {
        setPreferences(prefRes?.data?.data || null);
        const key = vapidRes?.data?.data?.publicKey || vapidRes?.data?.publicKey || process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '';
        setVapidPublicKey(key);
        const supportStatus = getPushSupportStatus();
        setPushSupport(supportStatus);
        setVapidReady(!!key);
        metaLoadedRef.current = true;
      }
      handleIncomingNotifications(items);
    } catch (error) {
      // Sessizce atlanıyor
    } finally {
      notificationFetchInProgressRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, [user, authLoading, calculateUnread]);

  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        await api.post('/api/notifications/mark-read', { notificationId });
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === notificationId ? { ...item, isRead: true, readAt: new Date().toISOString() } : item
          )
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (error) {
        toast.error('Bildirim güncellenemedi');
      }
    },
    []
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await api.post('/api/notifications/mark-all-read');
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true, readAt: item.readAt || new Date().toISOString() })));
      setUnreadCount(0);
    } catch (error) {
      toast.error('Bildirimler güncellenemedi');
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await api.delete(`/api/notifications/${notificationId}`);
      setNotifications((prev) => {
        const deleted = prev.find((item) => item.id === notificationId);
        if (deleted && !deleted.isRead) {
          setUnreadCount((count) => Math.max(count - 1, 0));
        }
        return prev.filter((item) => item.id !== notificationId);
      });
    } catch (error) {
      toast.error('Bildirim silinemedi');
    }
  }, []);

  const openNotificationPanel = useCallback(() => {
    window.dispatchEvent(new CustomEvent('openNotificationPanel'));
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return undefined;

    const unlockAudio = () => {
      if (!audioCtxRef.current) {
        audioCtxRef.current = new AudioContextClass();
      }
      if (audioCtxRef.current?.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      audioUnlockedRef.current = true;
    };

    window.addEventListener('pointerdown', unlockAudio, { passive: true });
    window.addEventListener('keydown', unlockAudio, { passive: true });

    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const playSound = useCallback(() => {
    if (typeof window === 'undefined') return;
    try {
      if (!audioUnlockedRef.current) return;
      if (!audioCtxRef.current) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) return;
        audioCtxRef.current = new AudioContext();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === 'suspended') {
        ctx.resume().catch(() => {});
        return;
      }
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = 880;
      osc.connect(gain);
      gain.connect(ctx.destination);
      const now = ctx.currentTime;
      gain.gain.setValueAtTime(0.001, now);
      gain.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.4);
      osc.start(now);
      osc.stop(now + 0.4);
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, []);

  const showToastForNotification = useCallback((notification) => {
    if (!notification || !notification.title) return;
    try {
      playSound();
    } catch (error) {
      // Sessizce atlanıyor
    }
    try {
      toast.custom(
        (t) => {
          if (!t || !t.id) return null;
          const handleClose = (e) => {
            try {
              e?.stopPropagation();
              toast.dismiss(t.id);
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          const handleClick = () => {
            try {
              toast.dismiss(t.id);
              // Müşteri siparişi bildirimleri için yönlendirme
              if (notification.type === 'customer_order' && notification.metadata?.redirectUrl) {
                window.location.href = notification.metadata.redirectUrl;
              } else {
                openNotificationPanel();
              }
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          return (
            <div className="pointer-events-auto max-w-sm w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-3 relative">
              <button
                onClick={handleClose}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition z-10"
                aria-label="Kapat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div
                onClick={handleClick}
                className="cursor-pointer pr-6"
              >
                <p className="text-xs text-gray-400 dark:text-gray-500">Yeni bildirim</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{notification.title || ''}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-line">
                  {notification.body || ''}
                </p>
              </div>
            </div>
          );
        },
        { duration: 5000 }
      );
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, [playSound, openNotificationPanel]);

  const handleIncomingNotifications = useCallback(
    (items) => {
      if (!items.length) return;
      const newestId = Math.max(...items.map((item) => Number(item.id) || 0));
      if (!Number.isFinite(newestId) || newestId <= 0) return;

      // Logout sonrası ref null kalabiliyor; storage'dan geri yükle
      if (lastSeenIdRef.current == null) {
        const storedId = readLastSeenIdFromStorage();
        if (storedId != null) {
          lastSeenIdRef.current = storedId;
        }
      }

      // İlk oturum yüklemesi: baseline al, eski bildirimler için toast atma
      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        lastSeenIdRef.current = Math.max(lastSeenIdRef.current || 0, newestId);
        writeLastSeenIdToStorage(lastSeenIdRef.current);
        return;
      }

      const lastSeen = lastSeenIdRef.current || 0;
      const newOnes = items.filter((item) => Number(item.id) > lastSeen);
      if (newOnes.length) {
        lastSeenIdRef.current = newestId;
        writeLastSeenIdToStorage(newestId);
        newOnes.reverse().forEach(showToastForNotification);
      }
    },
    [showToastForNotification, readLastSeenIdFromStorage, writeLastSeenIdToStorage]
  );

  const ensurePushSubscription = useCallback(async () => {
    const loadingToastId = 'push-enable-loading';
    if (pushSyncInProgressRef.current) {
      return { success: false };
    }

    pushSyncInProgressRef.current = true;
    try {
      toast.loading('Push bildirimleri etkinleştiriliyor...', {
        id: loadingToastId,
        duration: 15000
      });

      const supportStatus = typeof window !== 'undefined'
        ? getPushSupportStatus()
        : { supported: false };
      setPushSupport(supportStatus);

      if (!supportStatus?.supported) {
        const reason = supportStatus?.reason || 'unknown';
        const messages = {
          iosStandalone: 'iOS\'ta bildirim için Safari\'den "Paylaş > Ana Ekrana Ekle" ile uygulamayı yükleyin.',
          missingKey: 'Push anahtarları sunucuda tanımlı değil. Yöneticiye haber verin.',
          serviceWorker: 'Tarayıcınız service worker desteklemiyor. Chrome veya Edge kullanmayı deneyin.',
          notification: 'Tarayıcınız bildirim API\'sini desteklemiyor.'
        };
        toast.error(messages[reason] || 'Push bildirimleri bu cihazda etkinleştirilemiyor.', {
          id: loadingToastId,
          duration: 4000
        });
        return { success: false };
      }

      if (!vapidReady) {
        toast.error('Push anahtarları hazır değil; sayfayı yenileyip tekrar deneyin.', {
          id: loadingToastId,
          duration: 4000
        });
        return { success: false };
      }

      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'denied') {
        const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        toast.error(
          isMobile
            ? 'Bildirim izinleri engellenmiş. Telefon ayarlarından izin verin.'
            : 'Bildirim izinleri engellenmiş. Tarayıcı ayarlarından izin verin.',
          { id: loadingToastId, duration: 5000 }
        );
        return { success: false };
      }

      const registration = await registerServiceWorker();
      serviceWorkerReadyRef.current = true;

      if (!registration?.pushManager) {
        toast.error('Push Manager bu tarayıcıda desteklenmiyor.', {
          id: loadingToastId,
          duration: 4000
        });
        return { success: false };
      }

      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        toast.loading('Bildirim izni isteniyor...', {
          id: loadingToastId,
          duration: 8000
        });
      }

      const subscription = await getOrCreatePushSubscription(registration);
      const subscriptionData = serializePushSubscription(subscription);
      const response = await api.post('/api/notifications/push-subscription', subscriptionData);
      if (response?.data?.data) {
        setPreferences(response.data.data);
      }

      const deviceTypeText = subscriptionData.metadata?.deviceType === 'mobile'
        ? 'mobil cihazda'
        : 'web tarayıcıda';
      toast.success(`Push bildirimleri ${deviceTypeText} etkinleştirildi`, {
        id: loadingToastId,
        duration: 3000
      });

      if (user?.id) {
        pushSyncedUserRef.current = user.id;
      }
      fetchNotifications({ includeMeta: true });
      return { success: true };
    } catch (error) {
      toast.error(error?.message || 'Push bildirimi etkinleştirilemedi', {
        id: loadingToastId,
        duration: 4000
      });
      return { success: false, error };
    } finally {
      pushSyncInProgressRef.current = false;
    }
  }, [fetchNotifications, vapidReady, user]);

  const syncPushSubscription = useCallback(async () => {
    if (!user || authLoading || !vapidReady) return { success: false };
    if (pushSyncInProgressRef.current) return { success: false };
    if (typeof window === 'undefined' || !('Notification' in window)) return { success: false };
    if (Notification.permission !== 'granted') return { success: false };

    const supportStatus = getPushSupportStatus();
    if (!supportStatus.supported) return { success: false };

    pushSyncInProgressRef.current = true;
    try {
      const registration = await registerServiceWorker();
      serviceWorkerReadyRef.current = true;
      if (!registration?.pushManager) return { success: false };

      const subscription = await getOrCreatePushSubscription(registration);
      const subscriptionData = serializePushSubscription(subscription);
      const response = await api.post('/api/notifications/push-subscription', subscriptionData);
      if (response?.data?.data) {
        setPreferences(response.data.data);
      }
      pushSyncedUserRef.current = user.id;
      return { success: true };
    } catch (error) {
      // Sessiz sync — toast gösterme (mobilde takılı kalan hata toast'larını önler)
      return { success: false, error };
    } finally {
      pushSyncInProgressRef.current = false;
    }
  }, [user, authLoading, vapidReady]);

  useEffect(() => {
    if (!user || authLoading || !vapidReady || !metaLoadedRef.current) return;
    if (pushSyncedUserRef.current === user.id && hasPushSubscriptions(preferences)) return;

    const timeoutId = setTimeout(() => {
      syncPushSubscription();
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [user, authLoading, vapidReady, preferences, syncPushSubscription]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!user || !vapidReady) return;
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      if (!hasPushSubscriptions(preferences)) {
        syncPushSubscription();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [user, vapidReady, preferences, syncPushSubscription]);

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      setPreferences(null);
      setShowReminder(false);
      metaLoadedRef.current = false;
      // Sonraki login'de eski listeyi "yeni" sanmasın diye baseline bayrağını aç
      initialLoadRef.current = true;
      // lastSeen'i storage'da tut; ref'i de storage'dan koru (null'a çekme)
      const storedId = readLastSeenIdFromStorage();
      lastSeenIdRef.current = storedId;
      pushSyncedUserRef.current = null;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }
    
    const token = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
    if (!token) return;

    // Login: lastSeen'i storage'dan tazele; initialLoad'u fetch bitmeden false yapma
    if (lastSeenIdRef.current == null) {
      lastSeenIdRef.current = readLastSeenIdFromStorage();
    }
    initialLoadRef.current = true;
    
    const delay = 200;
    const timeoutId = setTimeout(() => {
      const currentToken = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
      if (!currentToken) return;
      
      fetchNotifications({ includeMeta: true });
      
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(() => {
        const checkToken = localStorage.getItem('jansoft-token') || sessionStorage.getItem('jansoft-token');
        if (checkToken && user) {
          fetchNotifications({ silent: true });
        }
      }, 30000);
    }, delay);
    
    return () => {
      clearTimeout(timeoutId);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [user, authLoading, fetchNotifications, readLastSeenIdFromStorage]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event) => {
      if (event.data?.type === 'NEW_NOTIFICATION') {
        fetchNotifications({ silent: true });
      }
      if (event.data?.type === 'OPEN_URL' && event.data?.url) {
        window.location.href = event.data.url;
      }
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const notificationId = event.data.notificationId;
        const url = event.data.url;
        if (notificationId) {
          markAsRead(notificationId);
        }
        if (url) {
          const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          if (current !== url) {
            window.location.href = url;
          }
        }
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handler);
    };
  }, [fetchNotifications, markAsRead]);

  // Push tıklayıp uygulama kapalıysa: ?markNotificationRead=ID
  useEffect(() => {
    if (!user || authLoading) return;
    try {
      const params = new URLSearchParams(window.location.search);
      const markId = params.get('markNotificationRead');
      if (!markId) return;
      const parsedId = parseInt(markId, 10);
      if (!Number.isFinite(parsedId)) return;

      markAsRead(parsedId).finally(() => {
        params.delete('markNotificationRead');
        const nextSearch = params.toString();
        const nextUrl =
          window.location.pathname +
          (nextSearch ? `?${nextSearch}` : '') +
          window.location.hash;
        window.history.replaceState({}, '', nextUrl);
      });
    } catch (_) {
      // sessiz
    }
  }, [user, authLoading, markAsRead]);

  useEffect(() => {
    const visibilityHandler = () => {
      if (document.visibilityState === 'visible') {
        fetchNotifications({ silent: true });
      }
    };
    document.addEventListener('visibilitychange', visibilityHandler);
    return () => document.removeEventListener('visibilitychange', visibilityHandler);
  }, [fetchNotifications]);

  useEffect(() => {
    if (vapidReady) {
      const newStatus = getPushSupportStatus();
      setPushSupport(newStatus);
    }
  }, [vapidReady]);

  // Hatırlatma kontrolü fonksiyonu
  const checkShouldShowReminder = useCallback(() => {
    // Kullanıcı yoksa veya sayfa görünür değilse hatırlatma gösterme
    if (!user || document.visibilityState !== 'visible') {
      return false;
    }

    // Okunmamış bildirim yoksa hatırlatma gösterme
    if (unreadCount === 0) {
      return false;
    }

    // Bugün hatırlatma kapatıldı mı kontrol et
    try {
      const dismissedToday = localStorage.getItem('jansoft-reminder-dismissed-today');
      if (dismissedToday) {
        const dismissedDate = new Date(dismissedToday);
        const today = new Date();
        if (
          dismissedDate.getDate() === today.getDate() &&
          dismissedDate.getMonth() === today.getMonth() &&
          dismissedDate.getFullYear() === today.getFullYear()
        ) {
          return false;
        }
      }
    } catch (error) {
      // localStorage hatası sessizce atlanıyor
    }

    // Son hatırlatmadan 5 dakika geçti mi kontrol et
    try {
      const lastReminder = localStorage.getItem('jansoft-last-notification-reminder');
      if (lastReminder) {
        const lastReminderTime = parseInt(lastReminder, 10);
        const now = Date.now();
        const fiveMinutes = 5 * 60 * 1000; // 5 dakika
        if (now - lastReminderTime < fiveMinutes) {
          return false;
        }
      }
    } catch (error) {
      // localStorage hatası sessizce atlanıyor
    }

    return true;
  }, [user, unreadCount]);

  // Hatırlatmayı göster
  const showUnreadReminder = useCallback(() => {
    if (checkShouldShowReminder()) {
      setShowReminder(true);
      try {
        localStorage.setItem('jansoft-last-notification-reminder', Date.now().toString());
      } catch (error) {
        // localStorage hatası sessizce atlanıyor
      }
    }
  }, [checkShouldShowReminder]);

  // Hatırlatmayı kapat (5 dakika sonra tekrar göster)
  const dismissReminder = useCallback(() => {
    setShowReminder(false);
    try {
      localStorage.setItem('jansoft-last-notification-reminder', Date.now().toString());
    } catch (error) {
      // localStorage hatası sessizce atlanıyor
    }
  }, []);

  // Bugün hatırlatmayı kapat (bugün bir daha gösterme)
  const dismissReminderForToday = useCallback(() => {
    setShowReminder(false);
    try {
      localStorage.setItem('jansoft-reminder-dismissed-today', new Date().toISOString());
      localStorage.setItem('jansoft-last-notification-reminder', Date.now().toString());
    } catch (error) {
      // localStorage hatası sessizce atlanıyor
    }
  }, []);

  // Periyodik hatırlatma kontrolü
  useEffect(() => {
    if (!user || authLoading) {
      if (reminderCheckIntervalRef.current) {
        clearInterval(reminderCheckIntervalRef.current);
        reminderCheckIntervalRef.current = null;
      }
      return;
    }

    // İlk kontrol
    if (unreadCount > 0 && !showReminder) {
      const timeoutId = setTimeout(() => {
        showUnreadReminder();
      }, 30000); // 30 saniye sonra ilk kontrol

      // Periyodik kontrol (her 1 dakikada bir)
      reminderCheckIntervalRef.current = setInterval(() => {
        if (!showReminder && checkShouldShowReminder()) {
          showUnreadReminder();
        }
      }, 60000); // 1 dakika

      return () => {
        clearTimeout(timeoutId);
        if (reminderCheckIntervalRef.current) {
          clearInterval(reminderCheckIntervalRef.current);
          reminderCheckIntervalRef.current = null;
        }
      };
    }
  }, [user, authLoading, unreadCount, showReminder, checkShouldShowReminder, showUnreadReminder]);

  // Sayfa görünürlüğü değiştiğinde hatırlatma kontrolü
  useEffect(() => {
    const reminderVisibilityHandler = () => {
      if (document.visibilityState === 'visible' && unreadCount > 0 && !showReminder) {
        setTimeout(() => {
          showUnreadReminder();
        }, 5000); // 5 saniye sonra kontrol
      }
    };
    document.addEventListener('visibilitychange', reminderVisibilityHandler);
    return () => document.removeEventListener('visibilitychange', reminderVisibilityHandler);
  }, [unreadCount, showReminder, showUnreadReminder]);

  const hasServerPushSubscription = hasPushSubscriptions(preferences);

  const value = {
    notifications,
    unreadCount,
    loading,
    preferences,
    refresh: () => fetchNotifications({ includeMeta: true }),
    markAsRead,
    markAllAsRead,
    deleteNotification,
    openNotificationPanel,
    ensurePushSubscription,
    pushSupport,
    hasServerPushSubscription,
    showReminder,
    setShowReminder,
    dismissReminder,
    dismissReminderForToday
  };

  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => useContext(NotificationContext);


